<template>
  <div class="password-confirm">
    <Form
      ref="formRef"
      :model="formState"
    >
      <FormItem
        :name="['password', 'first']"
        :rules="[{ required: true, validator: validatePass, trigger: 'blur' }]"
      >
        <InputPassword
          v-model:value="formState.password.first"
          class="password-input"
          type="password"
          :placeholder="firstPlaceholder"
          autocomplete="new-password"
        />
      </FormItem>
      <FormItem
        :name="['password', 'confirm']"
        :rules="[{ required: true, validator: validatePassConfirm, trigger: 'blur' }]"
      >
        <InputPassword
          v-model:value="formState.password.confirm"
          class="password-input"
          type="password"
          :placeholder="confirmPlaceholder"
          autocomplete="off"
        />
      </FormItem>

      <!-- <div v-if="showTip" class="password-tip">
        ⚠️密码需包含大写字母、小写字母、数字、特殊符号(如：#$%&()*等)
      </div> -->
    </Form>
  </div>
</template>

<script setup lang="ts">
import type { FormInstance } from 'ant-design-vue'
import type { Rule } from 'ant-design-vue/es/form'

import { validatePassword } from '@peerup/helper'
import { Form, FormItem, InputPassword } from 'ant-design-vue'
import { reactive, ref, watch } from 'vue'

interface Props {
  /** 第一个密码框的占位文本 */
  firstPlaceholder?: string
  /** 第二个密码框的占位文本 */
  confirmPlaceholder?: string
  /** 是否显示密码规则提示 */
  showTip?: boolean
  /** 密码框高度 */
  height?: number
  /** 密码框圆角 */
  borderRadius?: number
  /** 密码框背景色 */
  background?: string
  /** 第一个密码框的值 */
  first?: string
  /** 第二个密码框的值 */
  confirm?: string
  /** 国际化文本 */
  i18nText?: {
    请输入登录密码: string
    再次输入密码: string
    两次输入的密码不一致: string
  }
}

const props = withDefaults(defineProps<Props>(), {
  firstPlaceholder: '请输入登录密码',
  confirmPlaceholder: '再次输入密码',
  showTip: true,
  height: 44,
  borderRadius: 6,
  background: '#FFFFFF',
  first: '',
  confirm: '',
  i18nText: () => ({
    请输入登录密码: '请输入登录密码',
    再次输入密码: '再次输入密码',
    两次输入的密码不一致: '两次输入的密码不一致',
  }),
})

const emit = defineEmits<{
  (e: 'update:value', value: string): void
  (e: 'change', value: { valid: boolean; password: string }): void
}>()

interface FormState {
  password: {
    first: string
    confirm: string
  }
}

const formRef = ref<FormInstance>()
const formState = reactive<FormState>({
  password: {
    first: props.first || '',
    confirm: props.confirm || '',
  },
})

// 验证第一个密码
const validatePass = async (_rule: Rule, value: string) => {
  if (!value) {
    return Promise.reject(new Error(props.i18nText['请输入登录密码']))
  }
  await validatePassword(_rule, value)
  if (formState.password.confirm && value !== formState.password.confirm) {
    return Promise.reject(new Error(props.i18nText['两次输入的密码不一致']))
  }
  return Promise.resolve()
}

// 验证确认密码
const validatePassConfirm = async (_rule: Rule, value: string) => {
  if (!value) {
    return Promise.reject(new Error(props.i18nText['再次输入密码']))
  }
  if (value !== formState.password.first) {
    return Promise.reject(new Error(props.i18nText['两次输入的密码不一致']))
  }
  return Promise.resolve()
}

// 监听密码变化
watch(
  () => formState.password,
  async () => {
    try {
      const valid = await formRef.value?.validate().catch(() => false)
      if (!valid) return
      emit('change', { valid: true, password: formState.password.first })
      emit('update:value', formState.password.first)
    } catch (error) {
      console.error(error)
      emit('change', { valid: false, password: '' })
      emit('update:value', '')
    }
  },
  { deep: true },
)

// 重置表单
const resetForm = () => {
  formRef.value?.resetFields()
}

// 验证表单
const validate = async () => {
  await formRef.value?.validate()
  return formState.password.first
}

defineExpose({
  resetForm,
  validate,
})
</script>

<style lang="less" scoped>
.password-confirm {
  .password-input {
    height: v-bind('`${props.height}px`') !important;
  }

  .password-tip {
    margin-top: -8px;
    color: var(--functional-red-light-20);
  }
}
</style>
